import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { WawiListService } from "../../../../../Services/wawiList.service";
import { LoginService } from "../../../../../Services/login.service";
import { MappingType, WawiList } from "../../../../../Model/wawi/WawiList";
import { TranslateService } from "@ngx-translate/core";
import { FIELD_LIST, GROUP_LIST } from "../vaildUnitMappingFields";
import { SystemService } from "../../../../../Services/system.service";
import { DxDataGridComponent } from "devextreme-angular";
import { WawiListElement } from "../../../../../Model/wawi/WawiListElement";
import { GoldenRecordService } from "../../../../../Services/goldenRecord.service";
import { custom } from "devextreme/ui/dialog";

type Item = {
    parentId: string;
    field: string;
    translatedField?: string;
};

@Component({
    selector: 'np-others-mapping-edit',
    templateUrl: './np-others-mapping-edit.component.html',
    styleUrl: './np-others-mapping-edit.component.css',
    standalone: false
})
export class NpOthersMappingEditComponent implements OnInit, OnChanges {
    @ViewChild('mappingGrid', { static: false }) mappingGrid?: DxDataGridComponent;

    @Input() mapping!: WawiList;
    @Input() activeCatalogId: string;

    protected isLoading: boolean = false;

    private readonly MAX_SELECTED = 7;

    readonly MappingType = MappingType;

    fieldListTree: Item[] = [];

    constructor(
        public wawiListService: WawiListService,
        public loginService: LoginService,
        public translate: TranslateService,
        public systemService: SystemService,
        public goldenRecordService: GoldenRecordService,
    ) {
    }

    async ngOnInit() {
        this.fillDataSource();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['mapping']) {
            this.fillDataSource();
            if (this.mapping && !this.mapping.mappingFields)
                this.mapping.mappingFields = [];
            if (this.mapping && !this.mapping.fields)
                this.mapping.fields = [];
        }
    }

    get key() {
        return this.mapping?.key || this.translate.instant("Schlüssel");
    }

    get value() {
        return this.mapping?.value || this.translate.instant("Wert");
    }

    fillDataSource() {
        this.fieldListTree = GROUP_LIST.map(item => ({
            parentId: null,
            field: item.toString(),
            translatedField: this.translate.instant(item.toString())
        } as Item));

        FIELD_LIST.forEach(item => {
            let group = this.fieldListTree.find(x => x.field == item.group.toString());
            if (group != null) {
                this.fieldListTree.push({
                    parentId: group.field,
                    field: item.field.toString(),
                    translatedField: this.translate.instant(item.field.toString())
                } as Item);
            }
        });
    }

    private isFieldChild(field: string): boolean {
        let item = this.fieldListTree.find(x => x.field == field);
        return item != null && item.parentId != null;
    }

    get selectedChildCount(): number {
        return this.mapping.mappingFields.filter(k => this.isFieldChild(k)).length;
    }

    isSelected(item: Item): boolean {
        return this.mapping.mappingFields.includes(item.field.toString());
    }

    isLeafDisabled(item: Item): boolean {
        if (item.parentId == null)
            return true;
        if (this.isSelected(item))
            return false;
        return this.selectedChildCount >= this.MAX_SELECTED;
    }

    onFieldSelectionChanged(item: Item, e: any) {
        if (!e?.event)
            return;
        if (this.isSelected(item)) {
            this.mapping.mappingFields = this.mapping.mappingFields.filter(k => k != item.field.toString());
        } else {
            this.mapping.mappingFields.push(item.field.toString());
        }
    }

    async fillValueFromGoldenRecord() {
        if (!this.mapping.mappingFields?.length)
            return;

        this.isLoading = true;

        const goldenRecord = await this.goldenRecordService.getGoldenRecord(this.loginService.currentCustomer.id);
        if (!goldenRecord?.catalogId) {
            this.isLoading = false;
            return;
        }

        const goldenValues = await this.wawiListService.getCatalogFieldValues(
            goldenRecord.catalogId,
            this.loginService.currentCustomer.id,
            this.mapping.mappingFields
        );

        const goldenSet = new Set(goldenValues.map(v => v.trim()));
        let changed = false;

        for (const row of this.mapping.fields ?? []) {
            const key = (row.key ?? '').toString().trim();
            if (key && !row.value && goldenSet.has(key)) {
                row.value = key;
                changed = true;
            }
        }

        if (!changed) {
            this.systemService.notifyInfo(this.translate.instant("Keine passenden Einträge im Golden Record gefunden"));
            this.isLoading = false;
            return;
        }

        await this.onSaveMappings();
        this.mappingGrid?.instance?.refresh();
        this.isLoading = false;
    }

    async fillFromCatalog() {
        if (!this.activeCatalogId || !this.mapping.mappingFields?.length)
            return;

        this.isLoading = true;
        const values = await this.wawiListService.getCatalogFieldValues(
            this.activeCatalogId,
            this.loginService.currentCustomer.id,
            this.mapping.mappingFields
        );

        const existingKeys = new Set(
            (this.mapping.fields ?? []).map(f => (f.key ?? '').toString().trim()).filter(k => k.length > 0)
        );

        const toAdd = values.filter(v => v && !existingKeys.has(v.trim()));
        if (!toAdd.length) {
            this.systemService.notifyInfo(this.translate.instant("Keine neuen Einträge gefunden"));
            this.isLoading = false;
            return;
        }

        let maxId = (this.mapping.fields?.reduce((m, x) => Math.max(m, Number(x.id) || 0), 0) ?? 0);
        const newEntries: WawiListElement[] = toAdd.map(v => new WawiListElement(++maxId, v.trim(), ''));

        this.mapping.fields = [...(this.mapping.fields ?? []), ...newEntries];
        this.mapping.sourceValuesCatalogId = this.activeCatalogId;
        await this.onSaveMappings();

        this.mappingGrid?.instance?.refresh();
        this.isLoading = false;
    }

    async onRemoveField(field: string, event: Event) {
        event.stopPropagation();
        event.preventDefault();

        if (!this.mapping.mappingFields?.includes(field))
            return;

        if (this.mapping.mappingType == MappingType.FieldSpecific) {
            const confirmed = await this.confirmRemoveField();
            if (!confirmed)
                return;
        }

        this.mapping.mappingFields = this.mapping.mappingFields.filter(f => f != field);
    }

    private confirmRemoveField(): Promise<boolean> {
        const dialog = custom({
            title: this.translate.instant('Feld-Eintrag entfernen'),
            messageHtml: this.translate.instant('MappingRemoveFieldWarning'),
            buttons: [
                { text: this.translate.instant('Eintrag entfernen'), onClick: () => true },
                { text: this.translate.instant('Abbrechen'), onClick: () => false }
            ]
        });
        return dialog.show() as Promise<boolean>;
    }

    onRowInserting(e: any) {
        const maxId = (this.mapping.fields?.reduce((m, x) => Math.max(m, Number(x.id) || 0), 0) ?? 0);
        e.data.id = maxId + 1;
    }

    async onSaveMappings() {
        await this.wawiListService.update(this.mapping, this.loginService.currentCustomer.id);
    }
}
